import { inject, observer } from 'mobx-react';
import React, { Component, ComponentType } from 'react';
import styled from 'styled-components';

import { ImpuestosPredialCuentasStore } from '../../stores';

import { Table, TableNumberCell, TextBlock } from '../shared';

import { ColumnProps } from 'antd/lib/table';
import {
    ImpuestosPredialCuentasAcumuladosRecord,
    ImpuestosPredialCuentasDePagosRecord,
    ImpuestosPredialCuentasExistenteRecord,
    TableColumns,
    TableRecord
} from '../../models';
import { SubformSaveButton } from '../shared/SubformSaveButton';

interface CuentasSubformProps {
    isReadonly: boolean;
    impuestosPredialCuentasStore?: ImpuestosPredialCuentasStore;
}

const CuentasExistenteTable = (styled(Table)`
    width: 500px;
` as ComponentType) as new <T>() => Table<T>;

@inject('impuestosPredialCuentasStore')
@observer
export class CuentasSubform extends Component<CuentasSubformProps> {
    cuentasExistenteColumns: TableColumns<ImpuestosPredialCuentasExistenteRecord> = [
        {
            title: 'TIPO DE PREDIO',
            dataIndex: 'tipoDePredio',
            align: 'center',
            render: (text, record) => ({
                props: {
                    className: record.key === 'total' ? 'total-cell' : undefined
                },
                children: text
            })
        },
        {
            title: 'N° DE CUENTAS',
            dataIndex: 'numeroDeCuentas',
            align: 'center',
            width: 250,
            render: (_, record) => this.renderNumeroDeCuentas(record.key, record.numeroDeCuentas)
        }
    ];

    generateCuentasDePagosColumns = (
        ejercicio: number
    ): TableColumns<ImpuestosPredialCuentasDePagosRecord> =>
        this.generateCommonColumns<ImpuestosPredialCuentasDePagosRecord>(
            [
                `N° DE CUENTAS QUE REALIZARON ÚNICAMENTE PAGOS DE ${ejercicio}\\n(A)`,
                `N° DE CUENTAS QUE REALIZARON ÚNICAMENTE PAGOS DE EJERCICIOS ANTERIORES A ${ejercicio}\\n(B)`,
                `N° DE CUENTAS QUE REALIZARON TANTO PAGOS DE ${ejercicio} COMO DE EJERCICIOS ANTERIORES A ${ejercicio}\\n(C)`,
                `N° DE CUENTAS QUE REALIZARON PAGOS EN EL MES\\n(D=A+B+C)`
            ],
            [
                'numeroDePagosEjercicio',
                'numeroDePagosEjercicioAnterior',
                'numeroDePagosEjercicioMixto',
                'numeroDePagosTotal'
            ]
        );

    generateCuentasAcumuladosColumns = (ejercicio: number) =>
        this.generateCommonColumns<ImpuestosPredialCuentasAcumuladosRecord>(
            [
                `N° DE CUENTAS QUE REALIZARON ÚNICAMENTE PAGOS DE ${ejercicio}\\n(E)`,
                `N° DE CUENTAS QUE REALIZARON ÚNICAMENTE PAGOS DE EJERCICIOS ANTERIORES A ${ejercicio}\\n(F)`,
                `N° DE CUENTAS QUE REALIZARON TANTO PAGOS DE ${ejercicio} COMO DE EJERCICIOS ANTERIORES A ${ejercicio}\\n(G)`,
                `N° DE CUENTAS QUE REALIZARON PAGOS DEL 1 DE ENERO AL ÚLTIMO DÍA DEL MES\\n(H=E+F+G)`
            ],
            [
                'numeroDePagosAcumuladoEjercicio',
                'numeroDePagosAcumuladoEjercicioAnterior',
                'numeroDePagosAcumuladoEjercicioMixto',
                'numeroDePagosAcumuladoTotal'
            ]
        );

    generateCommonColumns = <T extends TableRecord>(
        titles: string[],
        keys: Array<keyof Pick<T, Exclude<keyof T, 'key'>>>
    ): TableColumns<T> =>
        keys.map<ColumnProps<T>>((key, index) => ({
            title: <TextBlock text={titles[index]} />,
            dataIndex: key as string,
            width: index < keys.length ? 200 : undefined,
            render:
                index < keys.length
                    ? (_, record) => this.renderNumeroDeCuentas(key as string, (record as any)[key])
                    : undefined
        }));

    renderNumeroDeCuentas = (path: string, value: number) => {
        const { isReadonly, impuestosPredialCuentasStore } = this.props;
        const { setCuentasValue } = impuestosPredialCuentasStore!;
        const isTotal = path.toLocaleLowerCase().includes('total');

        return (
            <TableNumberCell
                type="integer"
                isReadonly={isReadonly || isTotal}
                path={path}
                value={value}
                onChange={setCuentasValue}
            />
        );
    };

    render() {
        const { isReadonly, impuestosPredialCuentasStore } = this.props;
        const {
            isSaving,
            isLoading,
            ejercicio,
            cuentasExistenteRecords,
            cuentasDePagosRecords,
            cuentasDePagosAcumuladosRecords,
            saveCuentas
        } = impuestosPredialCuentasStore!;

        const { cuentasExistenteColumns } = this;
        const cuentasDePagosColumns = this.generateCuentasDePagosColumns(ejercicio);
        const cuentasAcumuladoColumns = this.generateCuentasAcumuladosColumns(ejercicio);

        return (
            <>
                <TextBlock text="CUADRO F2-A" />
                <TextBlock text="N° DE CUENTAS EXISTENTES EN EL PADRÓN DE IMPUESTO PREDIAL AL ÚLTIMO DÍA DEL MES" />
                <CuentasExistenteTable
                    dataSource={cuentasExistenteRecords}
                    columns={cuentasExistenteColumns}
                    loading={isLoading}
                />

                <TextBlock text="CUADRO F2-B" />
                <TextBlock text="N° DE CUENTAS QUE REALIZARON PAGOS EN EL MES\nPOR CONCEPTO DE IMPUESTO PREDIAL" />
                <Table
                    dataSource={cuentasDePagosRecords}
                    columns={cuentasDePagosColumns}
                    loading={isLoading}
                />

                <TextBlock text="CUADRO F2-C" />
                <TextBlock text="N° DE CUENTAS QUE REALIZARON PAGOS DEL 1 DE ENERO AL ÚLTIMO DÍA DE MES\nPOR CONCEPTO DE IMPUESTO PREDIAL" />
                <Table
                    dataSource={cuentasDePagosAcumuladosRecords}
                    columns={cuentasAcumuladoColumns}
                    loading={isLoading}
                />

                {!isReadonly && <SubformSaveButton isSaving={isSaving} onSave={saveCuentas} />}
            </>
        );
    }
}
